import type { FormRule } from '../types/block';
import type { Condition } from '../types/conditions';

/**
 * Collect field names referenced in a condition tree (row-relative or absolute paths).
 */
export function collectConditionFields(condition: Condition | undefined): string[] {
  if (condition == null) return [];

  if ('field' in condition && typeof condition.field === 'string') {
    return [condition.field];
  }
  if ('allOf' in condition) {
    return condition.allOf.flatMap((c) => collectConditionFields(c));
  }
  if ('anyOf' in condition) {
    return condition.anyOf.flatMap((c) => collectConditionFields(c));
  }
  if ('not' in condition) {
    return collectConditionFields(condition.not);
  }

  return [];
}

function ruleDependsOnWrittenFields(rule: FormRule, writtenFields: Set<string>): boolean {
  if (writtenFields.size === 0) return false;

  if (typeof rule.comparedTo === 'string' && writtenFields.has(rule.comparedTo)) {
    return true;
  }

  const conditionFields = collectConditionFields(rule.condition);
  return conditionFields.some((field) => writtenFields.has(field));
}

/**
 * Given per-row fields written by arrayEffects.perRow, derive row-relative targets
 * from itemRules that depend on those fields (comparedTo or condition).
 */
export function deriveArrayRevalidateTargets(
  itemRules: FormRule[] | undefined,
  writtenRowFieldNames: Iterable<string>,
): string[] {
  if (!itemRules?.length) return [];

  const written = new Set(writtenRowFieldNames);
  const targets = new Set<string>();

  for (const rule of itemRules) {
    if (!rule.target || rule.warning) continue;
    if (!ruleDependsOnWrittenFields(rule, written)) continue;
    targets.add(rule.target);
  }

  return [...targets];
}

/**
 * Given absolute paths written by effect.set, derive root rule targets that depend
 * on those paths (comparedTo exact match or condition field match).
 */
export function deriveRootRevalidateTargets(
  formRules: FormRule[] | undefined,
  writtenAbsolutePaths: Iterable<string>,
): string[] {
  if (!formRules?.length) return [];

  const written = new Set(writtenAbsolutePaths);
  const targets = new Set<string>();

  for (const rule of formRules) {
    if (!rule.target || rule.warning) continue;
    if (!ruleDependsOnWrittenFields(rule, written)) continue;
    targets.add(rule.target);
  }

  return [...targets];
}

/** Parse row index from `arrayName.{index}.field` path. */
export function parseRowIndexFromFieldPath(arrayName: string, fullPath: string): number | null {
  const prefix = `${arrayName}.`;
  if (!fullPath.startsWith(prefix)) return null;
  const rest = fullPath.slice(prefix.length);
  const dot = rest.indexOf('.');
  if (dot <= 0) return null;
  const index = Number.parseInt(rest.slice(0, dot), 10);
  return Number.isNaN(index) ? null : index;
}

/** Row-relative field name from `arrayName.{index}.field`. */
export function parseRowFieldNameFromPath(arrayName: string, fullPath: string): string | null {
  const prefix = `${arrayName}.`;
  if (!fullPath.startsWith(prefix)) return null;
  const rest = fullPath.slice(prefix.length);
  const dot = rest.indexOf('.');
  if (dot <= 0 || dot >= rest.length - 1) return null;
  return rest.slice(dot + 1);
}

export function collectWrittenRowFieldNames(
  arrayName: string,
  fieldsToWrite: string[],
): Set<string> {
  const names = new Set<string>();
  for (const path of fieldsToWrite) {
    const fieldName = parseRowFieldNameFromPath(arrayName, path);
    if (fieldName) names.add(fieldName);
  }
  return names;
}

export function collectAffectedRowIndices(arrayName: string, fieldsToWrite: string[]): number[] {
  const indices = new Set<number>();
  for (const path of fieldsToWrite) {
    const index = parseRowIndexFromFieldPath(arrayName, path);
    if (index != null) indices.add(index);
  }
  return [...indices].sort((a, b) => a - b);
}

export function buildArrayRevalidateTriggerPaths(
  arrayName: string,
  fieldsToWrite: string[],
  targets: string[],
): string[] {
  if (targets.length === 0 || fieldsToWrite.length === 0) return [];

  const indices = collectAffectedRowIndices(arrayName, fieldsToWrite);
  const paths: string[] = [];
  for (const index of indices) {
    for (const target of targets) {
      paths.push(`${arrayName}.${index}.${target}`);
    }
  }
  return paths;
}
